// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Eye product pages / widget (store front end). The Eye software
// itself is licensed separately under the Squarebit Eye End User License
// Agreement: https://store.squarebitstudios.com/squarebit-eye/eula
// ==========================================================================

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CodeBlock, MenuPath, Note, Tip, Warning } from '../../touchpose/ui';
import { H2, H3, Body, DocHero, DocFooter, Steps, UL_CLASS } from './shared';

// i18n contract for this page: every file name, folder name, environment
// variable, node/asset name and host menu path lives in a `components` slot,
// never inside the translated string — host UIs are English, so translating
// a menu path would make the instructions impossible to follow. Code blocks
// are siblings of the <Trans>, never children.
const LINK = 'text-blue-link no-underline font-medium';
const M = (s: string) => <MenuPath>{s}</MenuPath>;

const InstallationDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <DocHero
        title={t('eyeDocs.installation.title')}
        intro={t('eyeDocs.installation.intro')}
      />

      <H2 id="maya">{t('eyeDocs.layout.mayaArnold')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.mayaIntro"
          components={{
            f: M('Maya'),
            mll: M('squarebitEye.mll'),
            py: M('squarebit_eye')
          }}
        />
      </Body>
      <Steps>
        <><Trans
            i18nKey="eyeDocs.installation.mayaStep1"
            components={{
              f: M('Maya'),
              mod: M('squarebitEye.mod'),
              env: M('MAYA_MODULE_PATH'),
              file: M('Documents/maya/<version>/Maya.env')
            }}
          />
          <CodeBlock language="text">
            {'MAYA_MODULE_PATH = C:/tools/SquarebitEye/Maya'}
          </CodeBlock>
          {t('eyeDocs.installation.mayaStep1b')}</>
        <><Trans
            i18nKey="eyeDocs.installation.mayaStep2"
            components={{
              menu: M(
                'Windows ▸ Settings/Preferences ▸ Plug-in Manager ▸ squarebitEye.mll ▸ Loaded'
              ),
              b: <strong>Auto load</strong>
            }}
          /></>
        <>{t('eyeDocs.installation.mayaStep3')}
          <CodeBlock language="python">
            {`from squarebit_eye import tool
nodes = tool.create_eye_network(name="eyeL")`}
          </CodeBlock>
          <Trans
            i18nKey="eyeDocs.installation.mayaStep3b"
            components={{
              surf: M('standardSurface'),
              proj: M('eyeL_projector')
            }}
          /></>
      </Steps>
      <H3>Arnold</H3>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.arnoldIntro"
          components={{
            path: M('ARNOLD_PLUGIN_PATH'),
            node: <strong>aiSbeEye</strong>
          }}
        />
      </Body>
      <CodeBlock language="python">{'tool.add_arnold_shading(nodes)'}</CodeBlock>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.arnoldBody"
          components={{ i: <em /> }}
        />
      </Body>
      <Note label={t('eyeDocs.installation.verifyLabel')}>
        <Trans
          i18nKey="eyeDocs.installation.mayaVerify"
          components={{ b: <strong>6</strong>, proj: M('eyeL_projector') }}
        />
      </Note>

      <H2 id="blender">{t('eyeDocs.layout.blender')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.blenderIntro"
          components={{ b: <strong>Cycles</strong> }}
        />
      </Body>
      <Steps>
        <><Trans
            i18nKey="eyeDocs.installation.blenderStep1"
            components={{
              f: M('Blender'),
              menu: M('Edit ▸ Preferences ▸ Get Extensions ▸ Install from Disk…'),
              zip: M('squarebit_eye-<version>.zip')
            }}
          /></>
        <><Trans
            i18nKey="eyeDocs.installation.blenderStep2"
            components={{
              menu: M('Add ▸ Mesh ▸ Squarebit Eye'),
              empty: M('eye_*_projector')
            }}
          /></>
        <><Trans
            i18nKey="eyeDocs.installation.blenderStep3"
            components={{ b: <strong>Open Shading Language</strong> }}
          /></>
      </Steps>
      <Note label={t('eyeDocs.installation.verifyLabel')}>
        {t('eyeDocs.installation.blenderVerify')}
      </Note>

      <H2 id="unreal">{t('eyeDocs.layout.unreal')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.unrealIntro"
          components={{
            ml: M('M_SquarebitEye_L'),
            mr: M('M_SquarebitEye_R'),
            f: M('Unreal'),
            v57: M('UE5.7'),
            v58: M('UE5.8'),
            subdivs: <Link to="/squarebit-subdivs" className={LINK} />
          }}
        />
      </Body>
      <Steps>
        <><Trans
            i18nKey="eyeDocs.installation.unrealStep1"
            components={{
              f: M('SquarebitEye'),
              i: <em />,
              p57: M('Unreal/UE5.7/SquarebitEye'),
              p58: M('Unreal/UE5.8/SquarebitEye'),
              plugins: M('Plugins'),
              tree: M('<YourProject>/Plugins/SquarebitEye/'),
              menu: M('Edit ▸ Plugins ▸ Project ▸ SquarebitEye')
            }}
          /></>
        <><Trans
            i18nKey="eyeDocs.installation.unrealStep2"
            components={{
              mi: <strong>Material Instance</strong>,
              ml: M('M_SquarebitEye_L'),
              mr: M('M_SquarebitEye_R'),
              grp: <strong>Squarebit Eye</strong>
            }}
          /></>
        <><Trans
            i18nKey="eyeDocs.installation.unrealStep3"
            components={{
              comp: <strong>Squarebit Eye</strong>,
              axis: <strong />,
              rig: (
                <Link
                  to="/squarebit-eye/docs/rigging#unreal-rig"
                  className={LINK}
                />
              )
            }}
          /></>
      </Steps>
      <Tip label={t('eyeDocs.installation.tipRegenLabel')}>
        <Trans
          i18nKey="eyeDocs.installation.tipRegen"
          components={{ cmd: M('SquarebitEyeGenMaterials') }}
        />
        <CodeBlock language="powershell">
          {'UnrealEditor-Cmd.exe "<YourProject>.uproject" -run=SquarebitEyeGenMaterials -unattended -nopause -nosplash -AllowCommandletRendering'}
        </CodeBlock>
      </Tip>
      <Note label={t('eyeDocs.installation.verifyLabel')}>
        {t('eyeDocs.installation.unrealVerify')}
      </Note>

      <H2 id="web">{t('eyeDocs.layout.web')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.webIntro"
          components={{ mat: M('MeshPhysicalMaterial') }}
        />
      </Body>
      <CodeBlock language="javascript">
        {`import { loadEyeCore, loadSssDefaults, makeEyeUniforms, applyEyeShader,
         updateProjector, updateLightDir, updateEyeParams }
  from './eye-material.js';

const core = await loadEyeCore('core/eye_core.glsl');

// SPEC §5 sclera subsurface — the same weight and warm tint every host ships.
// Leave it out and the sclera renders with subsurface OFF (weight 0).
const sss  = await loadSssDefaults('materialx/sss_defaults.json');

const eye  = makeEyeUniforms({ irisMap, scleraMap, sss, params: { ior: 1.376 } });
applyEyeShader(myMeshPhysicalMaterial, core, eye);

// per frame — the projector is any Object3D; lookAt aims its +Z.
// opts takes any animation channel a rig keys: pupilRadius, lodBlend,
// irisWidth, irisHeight, pupilWidth, pupilHeight, pupilOffsetX, pupilOffsetY.
updateProjector(eye, projectorObject3D, { pupilRadius, lodBlend });
updateLightDir(eye, keyLight);`}
      </CodeBlock>
      <Warning label={t('eyeDocs.installation.webSssLabel')}>
        <Trans
          i18nKey="eyeDocs.installation.webSss"
          components={{
            fn: M('loadSssDefaults'),
            file: M('materialx/sss_defaults.json'),
            mk: M('makeEyeUniforms'),
            sss: M('sss')
          }}
        />
      </Warning>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.installation.webSssInline"
            components={{ vals: M('{ weight: 0.25, color: [0.96, 0.9, 0.86] }') }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.installation.webB1"
            components={{ o: M('Object3D'), la: M('lookAt') }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.installation.webB2"
            components={{
              opt: M('{ displace: true }'),
              fn: M('applyEyeShader'),
              params: M('params: { displacementWeight: 1, refractionMode: 1 }')
            }}
          />
        </li>
      </ul>
      <Note label={t('eyeDocs.installation.verifyLabel')}>
        {t('eyeDocs.installation.webVerify')}
      </Note>

      <H2 id="usd">{t('eyeDocs.layout.usd')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.usdIntro"
          components={{
            i: <em />,
            asset: M('SquarebitEye.usda'),
            prev: M('SquarebitEye_preview.usda'),
            mtlx: M('sbe_eye_std.mtlx'),
            tex: M('textures/')
          }}
        />
      </Body>
      <ul className={UL_CLASS}>
        <li>{t('eyeDocs.installation.usdB1')}</li>
        <li>
          <Trans
            i18nKey="eyeDocs.installation.usdB2a"
            components={{ b: <strong /> }}
          />
          <CodeBlock language="text">
            {'def "MyEye" (prepend references = @.../SquarebitEye.usda@) {}'}
          </CodeBlock>
          <Trans
            i18nKey="eyeDocs.installation.usdB2b"
            components={{
              kind: M('component'),
              prim: M('/SquarebitEye'),
              mesh: M('/SquarebitEye/Eyeball')
            }}
          />
        </li>
        <li>{t('eyeDocs.installation.usdB3')}</li>
      </ul>
      <Note label={t('eyeDocs.installation.verifyLabel')}>
        <Trans
          i18nKey="eyeDocs.installation.usdVerify"
          components={{
            prev: M('SquarebitEye_preview.usda'),
            usdview: M('usdview')
          }}
        />
      </Note>
      <H3>{t('eyeDocs.installation.h3Storm')}</H3>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.stormBody"
          components={{
            i: <em />,
            hi: <strong>High</strong>,
            f: M('Storm'),
            glslfx: M('glslfx'),
            file: M('sbe_eye.glslfx'),
            stage: M('sbe_eye_storm.usda')
          }}
        />
      </Body>

      <H2 id="houdini">{t('eyeDocs.layout.houdini')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.houdiniIntro"
          components={{ f: M('Houdini') }}
        />
      </Body>
      <Steps>
        <><Trans
            i18nKey="eyeDocs.installation.houdiniStep1"
            components={{ hip: M('SquarebitEye_karma.hip'), stage: M('/stage') }}
          /></>
        <><Trans
            i18nKey="eyeDocs.installation.houdiniStep2"
            components={{
              cpu: <strong>Karma CPU</strong>,
              xpu: <strong>Karma XPU</strong>
            }}
          /></>
        <><Trans
            i18nKey="eyeDocs.installation.houdiniStep3"
            components={{
              lop: <strong>Reference LOP</strong>,
              asset: M('SquarebitEye.usda')
            }}
          /></>
      </Steps>
      <Note label={t('eyeDocs.installation.verifyLabel')}>
        {t('eyeDocs.installation.houdiniVerify')}
      </Note>
      <Body>{t('eyeDocs.installation.houdiniKarmaRoute')}</Body>
      <H3>{t('eyeDocs.installation.h3HoudiniGl')}</H3>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.houdiniGlBody"
          components={{ f: M('Houdini'), hda: M('sbe_eye_glsl.hda') }}
        />
      </Body>
      <CodeBlock language="python">
        {`import hou
hou.hda.installFile("<download>/Houdini/houdini/sbe_eye_glsl.hda")
mat = hou.node("/mat").createNode("sbe::eye_glsl", "sbe_eye")
hou.node("/obj/eye").parm("shop_materialpath").set(mat.path())`}
      </CodeBlock>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.houdiniGlBody2"
          components={{
            txt: M('INSTALL.txt'),
            gl: <strong>OpenGL</strong>,
            menu: M('Edit ▸ Preferences ▸ 3D Viewports'),
            env: M('HOUDINI_VULKAN_VIEWER=0')
          }}
        />
      </Body>
      <Note label={t('eyeDocs.installation.verifyLabel')}>
        {t('eyeDocs.installation.houdiniGlVerify')}
      </Note>
      <H3>{t('eyeDocs.installation.h3HoudiniStorm')}</H3>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.houdiniStormBody"
          components={{
            storm: <strong>Storm</strong>,
            stageNet: M('Stage'),
            f: M('Storm'),
            stage: M('sbe_eye_storm.usda'),
            glslfx: M('glslfx')
          }}
        />
      </Body>
      <Note label={t('eyeDocs.installation.verifyLabel')}>
        <Trans
          i18nKey="eyeDocs.installation.houdiniStormVerify"
          components={{ storm: <strong>Storm</strong> }}
        />
      </Note>

      <H2 id="omniverse">{t('eyeDocs.installation.h2Omniverse')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.omniIntro"
          components={{
            mdl: <strong>MDL</strong>,
            file: M('sbe_eye.mdl'),
            f: M('Omniverse'),
            ext: M('squarebit.eye.deformer')
          }}
        />
      </Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.installation.omniB1"
            components={{
              asset: M('SquarebitEye.usda'),
              cc: M('catmullClark')
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.installation.omniB2"
            components={{
              def: <strong>Warp/OmniGraph</strong>,
              ext: M('squarebit.eye.deformer')
            }}
          />
        </li>
        <li>{t('eyeDocs.installation.omniB3')}</li>
      </ul>
      <Note label={t('eyeDocs.installation.verifyLabel')}>
        {t('eyeDocs.installation.omniVerify')}
      </Note>

      <H2 id="materialx">{t('eyeDocs.installation.h2MaterialX')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.installation.mtlxBody"
          components={{
            f: M('MaterialX'),
            i: <em />,
            file: M('sbe_eye.mtlx'),
            txt: M('INSTALL.txt'),
            arnold: <strong>Arnold</strong>,
            redshift: <strong>Redshift</strong>
          }}
        />
      </Body>
      <Note label={t('eyeDocs.installation.noteBulgeLabel')}>
        <Trans
          i18nKey="eyeDocs.installation.noteBulge"
          components={{ i: <em /> }}
        />
      </Note>
      <Note label={t('eyeDocs.installation.verifyLabel')}>
        <Trans
          i18nKey="eyeDocs.installation.mtlxVerify"
          components={{
            txt: M('INSTALL.txt'),
            scene: M('sbe_eye_usdview.usda')
          }}
        />
      </Note>

      <DocFooter current="/squarebit-eye/docs/installation" />
    </article>
  );
};

export default InstallationDoc;
